import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HomeSearchState } from '../components/HomeSearchBar';
import { MapExploreCategory } from '../components/MapCategoryPicker';
import { IMapBounds, IMapPin } from '../components/SpacesMap';
import OperatorFiltersModal from '../components/OperatorFiltersModal';
import AccessoryFiltersModal from '../components/AccessoryFiltersModal';
import ServiceFiltersModal from '../components/ServiceFiltersModal';
import HeavyDutyMachineryCard from '../pages/HeavyDutyMachinery/HeavyDutyMachineryCard';
import OperatorSearchCard from '../pages/HeavyDutyMachinery/OperatorSearchCard';
import AccessorySearchCard from '../pages/HeavyDutyMachinery/AccessorySearchCard';
import ServiceSearchCard from '../pages/HeavyDutyMachinery/ServiceSearchCard';
import { useMapCategorySearch } from './useMapCategorySearch';
import { toMachineryMapPin } from '../helpers/machineryMapPin';
import {
  accessoryPinId,
  operatorPinId,
  servicePinId,
  toAccessoryMapPin,
  toOperatorMapPin,
  toServiceMapPin,
} from '../helpers/categoryMapPins';
import { boundingBoxFromPoint } from '../helpers/geoBoundingBox';
import { IHeavyDutyMachinery } from '../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { IOperatorSearchFilters, IOperatorSearchResult } from '../interfaces/heavyDutyMachinery/IOperator';
import { IAccessorySearchFilters, IAccessorySearchResult } from '../interfaces/heavyDutyMachinery/IMachineryAccessory';
import { IServiceSearchFilters, IServiceSearchResult } from '../interfaces/heavyDutyMachinery/IService';
import { searchOperators } from '../services/OperatorService';
import { searchAccessories } from '../services/MachineryAccessoryService';
import { searchServices } from '../services/ServiceService';
import { getServiceCategoryById, getServiceCategoryBySlug } from '../constants/serviceOptions';
import { TRANSLATIONS } from '../constants/translations';

const DEFAULT_SEARCH_PAGE_SIZE = 20;

/** One row of results: the card, plus the same id the map's own pin carries. */
export interface MapCategoryRow {
  pinId: string;
  card: React.ReactNode;
  /** The tighter rendering for a map popup -- see each card's own `compact` prop. */
  compactCard: React.ReactNode;
}

/** Everything a map screen needs about whichever category is showing. */
export interface MapCategoryView {
  /** Only ever read for `.length` -- each category's real item type stays inside its own pipeline. */
  items: unknown[];
  pins: IMapPin[];
  loading: boolean;
  totalCount: number;
  filterCount: number;
  /** [plural, singular]. */
  resultsSuffixKeys: [string, string];
  loadingMessageKey: string;
  onBoundsChange: (bounds: IMapBounds) => void;
  openFilters: () => void;
  /** What a tap on a pin, a popup card or a list row leads to. */
  openItem: (pinId: string) => void;
  rows: MapCategoryRow[];
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  searchBarMode: MapExploreCategory;
  searchBarInitialValues: HomeSearchState;
  onSearchBarSubmit: (values: HomeSearchState) => void;
  locationSearchSeq: number;
}

interface UseMapCategoryViewsOptions {
  /**
   * Machinery's own pipeline, which this hook never owns: it lives in
   * HeavyDutyMachineries.tsx, where the URL is the source of truth (a
   * machinery search is deep-linkable and survives a refresh, which the other
   * three are not -- they are a viewport and a tab).
   */
  machinery: {
    items: IHeavyDutyMachinery[];
    loading: boolean;
    totalCount: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    activeFilterCount: number;
    onOpenFilters: () => void;
    searchBarInitialValues: HomeSearchState;
    onSearchBarSubmit: (values: HomeSearchState) => void;
    onBoundsChange: (bounds: IMapBounds) => void;
    onOpenSpace: (spaceId: string) => void;
    locationSearchSeq: number;
  };
  /** Which row/pin is hovered, so a machinery card can render in its hover state. */
  hoveredItemId?: string;
}

/**
 * The four categories a map can show -- machinery, accessories, operators,
 * services -- as one table, plus their filter overlays.
 *
 * In a hook because BOTH maps show all four now: the mobile map+sheet and the
 * desktop list+panel. Written inside either screen it would be written inside
 * both, and the second copy is where a fifth category (or a fix) lands in one
 * and not the other.
 *
 * What it does NOT own is machinery, and that is deliberate rather than
 * awkward: a machinery search lives in the URL (deep-linkable, survives a
 * refresh, shared by both screens and by the homepage's own search bar), so it
 * is passed in. The other three are a tab and a viewport -- nothing worth
 * putting in a URL -- so they own their own state here and go quiet the moment
 * their tab is not the active one.
 */
export const useMapCategoryViews = ({ machinery, hoveredItemId }: UseMapCategoryViewsOptions) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeCategory, setActiveCategory] = useState<MapExploreCategory>('machinery');

  const [operatorFiltersOpen, setOperatorFiltersOpen] = useState(false);
  const [accessoryFiltersOpen, setAccessoryFiltersOpen] = useState(false);
  const [serviceFiltersOpen, setServiceFiltersOpen] = useState(false);

  const operators = useMapCategorySearch<IOperatorSearchResult, IOperatorSearchFilters>(
    activeCategory === 'operators',
    { page: 1, pageSize: DEFAULT_SEARCH_PAGE_SIZE },
    searchOperators
  );
  const accessories = useMapCategorySearch<IAccessorySearchResult, IAccessorySearchFilters>(
    activeCategory === 'accessories',
    { page: 1, pageSize: DEFAULT_SEARCH_PAGE_SIZE },
    searchAccessories
  );
  const services = useMapCategorySearch<IServiceSearchResult, IServiceSearchFilters>(
    activeCategory === 'services',
    { page: 1, pageSize: DEFAULT_SEARCH_PAGE_SIZE },
    searchServices
  );

  // Bumped whenever the search bar resolves a point (a city suggestion, or
  // "near me") in one of the three tabs this hook owns -- an explicit event
  // rather than a derived value, same as machinery's own. It tells the map to
  // fly there; a plain filter change must not.
  const [categoryLocationSearchSeq, setCategoryLocationSearchSeq] = useState(0);

  /**
   * The last viewport any category was searched in, remembered so that
   * switching tabs can carry it across.
   *
   * Without this, changing tab searches the newly-active category with no
   * bounds at all -- the whole country, paged -- while the map still shows the
   * street somebody was looking at. They asked "what services are HERE", and
   * the honest answer to that needs the here.
   */
  const lastBoundsRef = useRef<IMapBounds | undefined>(undefined);
  const rememberBounds = (bounds: IMapBounds, apply: (bounds: IMapBounds) => void) => {
    lastBoundsRef.current = bounds;
    apply(bounds);
  };

  const switchCategory = (category: MapExploreCategory) => {
    setActiveCategory(category);

    const bounds = lastBoundsRef.current;
    if (!bounds) return;
    if (category === 'operators') operators.setFilters((prev) => ({ ...prev, ...bounds, page: 1 }));
    else if (category === 'accessories') accessories.setFilters((prev) => ({ ...prev, ...bounds, page: 1 }));
    else if (category === 'services') services.setFilters((prev) => ({ ...prev, ...bounds, page: 1 }));
    // Machinery's own bounds live in the parent's filters and are already
    // whatever this viewport last reported -- nothing to seed.
  };

  /**
   * The bar submits the same HomeSearchState shape in every mode -- only
   * `category` and the location are ever set outside machinery. Where that one
   * category value LANDS differs per tab, and each is a different field for a
   * real reason: an operator's is an array (the filter modal's own tile grid
   * edits it), an accessory's is an AccessoryType code (a different question
   * from "which machine it fits", which is that modal's own `category`), and a
   * service's is a trade slug the endpoint wants as a numeric id.
   */
  const handleSearchBarSubmit = (values: HomeSearchState) => {
    const boundsPatch =
      values.lat != null && values.lng != null && values.radiusKm != null
        ? boundingBoxFromPoint(values.lat, values.lng, values.radiusKm)
        : undefined;

    if (activeCategory === 'operators') {
      operators.setFilters((prev) => ({ ...prev, category: values.category ? [values.category] : undefined, page: 1, ...boundsPatch }));
    } else if (activeCategory === 'accessories') {
      accessories.setFilters((prev) => ({ ...prev, accessoryTypeCode: values.category, page: 1, ...boundsPatch }));
    } else if (activeCategory === 'services') {
      services.setFilters((prev) => ({ ...prev, serviceCategoryId: getServiceCategoryBySlug(values.category)?.id, page: 1, ...boundsPatch }));
    }

    if (boundsPatch) setCategoryLocationSearchSeq((seq) => seq + 1);
  };

  // Reflected back into the bar, so switching tabs away and back does not show
  // a blank search bar over an already-filtered result set. Only `category`
  // round-trips: these three track no named place the way machinery's own
  // `city` does, just the raw viewport.
  const operatorInitialValues: HomeSearchState = useMemo(() => ({ category: operators.filters.category?.[0] }), [operators.filters.category]);
  const accessoryInitialValues: HomeSearchState = useMemo(
    () => ({ category: accessories.filters.accessoryTypeCode }),
    [accessories.filters.accessoryTypeCode]
  );
  const serviceInitialValues: HomeSearchState = useMemo(
    () => ({ category: getServiceCategoryById(services.filters.serviceCategoryId)?.slug }),
    [services.filters.serviceCategoryId]
  );

  // Neither an operator nor an accessory has a standalone detail/booking page
  // today -- renting either on its own is a later phase (see
  // MachineryAccessory.IsIndependentlyRentable). The only already-built way to
  // book one is through the specific machine it is shown at, so a tap goes
  // there. A service is different: it IS hired on its own, so it opens its own
  // page, where the guest either checks out or asks for a quote.
  const openOperatorsMachinery = (pinId: string) => {
    const operator = operators.items.find((o) => operatorPinId(o) === pinId);
    if (operator) navigate(`/heavy-duty-machinery/${operator.machineryId}`);
  };
  const openAccessorysMachinery = (pinId: string) => {
    const accessory = accessories.items.find((a) => accessoryPinId(a) === pinId);
    if (accessory) navigate(`/heavy-duty-machinery/${accessory.machineryId}`);
  };
  const openService = (serviceId: string) => navigate(`/heavy-duty-machinery/services/${serviceId}`);

  const views: Record<MapExploreCategory, MapCategoryView> = {
    machinery: {
      items: machinery.items,
      pins: machinery.items.map(toMachineryMapPin),
      loading: machinery.loading,
      totalCount: machinery.totalCount,
      filterCount: machinery.activeFilterCount,
      resultsSuffixKeys: [
        TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_RESULTS_SUFFIX,
        TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_RESULTS_SUFFIX_SINGULAR,
      ],
      loadingMessageKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MOBILE_LOADING_MESSAGE,
      onBoundsChange: (bounds) => rememberBounds(bounds, machinery.onBoundsChange),
      openFilters: machinery.onOpenFilters,
      openItem: machinery.onOpenSpace,
      rows: machinery.items.map((space) => ({
        pinId: space.id,
        card: <HeavyDutyMachineryCard space={space} onClick={() => machinery.onOpenSpace(space.id)} forceHover={hoveredItemId === space.id} />,
        compactCard: <HeavyDutyMachineryCard compact space={space} onClick={() => machinery.onOpenSpace(space.id)} />,
      })),
      page: machinery.page,
      pageSize: machinery.pageSize,
      setPage: machinery.onPageChange,
      setPageSize: machinery.onPageSizeChange,
      searchBarMode: 'machinery',
      searchBarInitialValues: machinery.searchBarInitialValues,
      onSearchBarSubmit: machinery.onSearchBarSubmit,
      locationSearchSeq: machinery.locationSearchSeq,
    },
    accessories: {
      items: accessories.items,
      pins: accessories.items.map(toAccessoryMapPin),
      loading: accessories.loading,
      totalCount: accessories.totalCount,
      filterCount:
        (accessories.filters.category?.length ?? 0) +
        (accessories.filters.minPricePerDay != null || accessories.filters.maxPricePerDay != null ? 1 : 0) +
        (accessories.filters.couplerType != null ? 1 : 0) +
        (accessories.filters.requiresHydraulics != null ? 1 : 0) +
        (accessories.filters.machineWeightTons != null ? 1 : 0) +
        (accessories.filters.minWorkingWidthMm != null || accessories.filters.maxWorkingWidthMm != null ? 1 : 0) +
        (accessories.filters.minCapacityLiters != null || accessories.filters.maxCapacityLiters != null ? 1 : 0) +
        (accessories.filters.minOwnWeightKg != null || accessories.filters.maxOwnWeightKg != null ? 1 : 0),
      resultsSuffixKeys: [TRANSLATIONS.ACCESSORIES_SEARCH_RESULTS_SUFFIX, TRANSLATIONS.ACCESSORIES_SEARCH_RESULTS_SUFFIX_SINGULAR],
      loadingMessageKey: TRANSLATIONS.ACCESSORIES_SEARCH_MOBILE_LOADING_MESSAGE,
      onBoundsChange: (bounds) => rememberBounds(bounds, (next) => accessories.setFilters((prev) => ({ ...prev, ...next }))),
      openFilters: () => setAccessoryFiltersOpen(true),
      openItem: openAccessorysMachinery,
      rows: accessories.items.map((accessory) => {
        const pinId = accessoryPinId(accessory);
        return {
          pinId,
          card: <AccessorySearchCard accessory={accessory} onClick={() => openAccessorysMachinery(pinId)} />,
          compactCard: <AccessorySearchCard compact accessory={accessory} onClick={() => openAccessorysMachinery(pinId)} />,
        };
      }),
      page: accessories.filters.page ?? 1,
      pageSize: accessories.filters.pageSize ?? DEFAULT_SEARCH_PAGE_SIZE,
      setPage: (page) => accessories.setFilters((prev) => ({ ...prev, page })),
      setPageSize: (pageSize) => accessories.setFilters((prev) => ({ ...prev, pageSize })),
      searchBarMode: 'accessories',
      searchBarInitialValues: accessoryInitialValues,
      onSearchBarSubmit: handleSearchBarSubmit,
      locationSearchSeq: categoryLocationSearchSeq,
    },
    operators: {
      items: operators.items,
      pins: operators.items.map(toOperatorMapPin),
      loading: operators.loading,
      totalCount: operators.totalCount,
      filterCount:
        (operators.filters.category?.length ?? 0) + (operators.filters.minHourlyRate != null || operators.filters.maxHourlyRate != null ? 1 : 0),
      resultsSuffixKeys: [TRANSLATIONS.OPERATORS_SEARCH_RESULTS_SUFFIX, TRANSLATIONS.OPERATORS_SEARCH_RESULTS_SUFFIX_SINGULAR],
      loadingMessageKey: TRANSLATIONS.OPERATORS_SEARCH_MOBILE_LOADING_MESSAGE,
      onBoundsChange: (bounds) => rememberBounds(bounds, (next) => operators.setFilters((prev) => ({ ...prev, ...next }))),
      openFilters: () => setOperatorFiltersOpen(true),
      openItem: openOperatorsMachinery,
      rows: operators.items.map((operator) => {
        const pinId = operatorPinId(operator);
        return {
          pinId,
          card: <OperatorSearchCard operator={operator} onClick={() => openOperatorsMachinery(pinId)} />,
          compactCard: <OperatorSearchCard compact operator={operator} onClick={() => openOperatorsMachinery(pinId)} />,
        };
      }),
      page: operators.filters.page ?? 1,
      pageSize: operators.filters.pageSize ?? DEFAULT_SEARCH_PAGE_SIZE,
      setPage: (page) => operators.setFilters((prev) => ({ ...prev, page })),
      setPageSize: (pageSize) => operators.setFilters((prev) => ({ ...prev, pageSize })),
      searchBarMode: 'operators',
      searchBarInitialValues: operatorInitialValues,
      onSearchBarSubmit: handleSearchBarSubmit,
      locationSearchSeq: categoryLocationSearchSeq,
    },
    services: {
      items: services.items,
      pins: services.items.map(toServiceMapPin),
      loading: services.loading,
      totalCount: services.totalCount,
      filterCount:
        (services.filters.serviceCategoryId != null ? 1 : 0) +
        (services.filters.requestMode != null ? 1 : 0) +
        (services.filters.pricingMode != null ? 1 : 0),
      resultsSuffixKeys: [TRANSLATIONS.SERVICES_SEARCH_RESULTS_SUFFIX, TRANSLATIONS.SERVICES_SEARCH_RESULTS_SUFFIX_SINGULAR],
      loadingMessageKey: TRANSLATIONS.SERVICES_SEARCH_MOBILE_LOADING_MESSAGE,
      onBoundsChange: (bounds) => rememberBounds(bounds, (next) => services.setFilters((prev) => ({ ...prev, ...next }))),
      openFilters: () => setServiceFiltersOpen(true),
      openItem: openService,
      rows: services.items.map((service) => ({
        pinId: servicePinId(service),
        card: <ServiceSearchCard service={service} onClick={() => openService(service.id)} />,
        compactCard: <ServiceSearchCard compact service={service} onClick={() => openService(service.id)} />,
      })),
      page: services.filters.page ?? 1,
      pageSize: services.filters.pageSize ?? DEFAULT_SEARCH_PAGE_SIZE,
      setPage: (page) => services.setFilters((prev) => ({ ...prev, page })),
      setPageSize: (pageSize) => services.setFilters((prev) => ({ ...prev, pageSize })),
      searchBarMode: 'services',
      searchBarInitialValues: serviceInitialValues,
      onSearchBarSubmit: handleSearchBarSubmit,
      locationSearchSeq: categoryLocationSearchSeq,
    },
  };

  /**
   * The three filter overlays, ready to drop at the end of a screen's tree.
   * Clearing keeps the viewport: the guest asked to drop their filters, not to
   * be teleported out of the area they are looking at.
   *
   * Machinery's own FiltersModal is NOT here -- it belongs to whoever owns the
   * machinery pipeline, which is not this hook.
   */
  const filtersModals = (
    <>
      <OperatorFiltersModal
        open={operatorFiltersOpen}
        onClose={() => setOperatorFiltersOpen(false)}
        filters={operators.filters}
        totalCount={operators.totalCount}
        onChange={(patch) => operators.setFilters((prev) => ({ ...prev, ...patch, page: 1 }))}
        onClear={() =>
          operators.setFilters((prev) => ({ minLat: prev.minLat, maxLat: prev.maxLat, minLng: prev.minLng, maxLng: prev.maxLng, page: 1, pageSize: prev.pageSize }))
        }
      />
      <AccessoryFiltersModal
        open={accessoryFiltersOpen}
        onClose={() => setAccessoryFiltersOpen(false)}
        filters={accessories.filters}
        totalCount={accessories.totalCount}
        onChange={(patch) => accessories.setFilters((prev) => ({ ...prev, ...patch, page: 1 }))}
        onClear={() =>
          accessories.setFilters((prev) => ({ minLat: prev.minLat, maxLat: prev.maxLat, minLng: prev.minLng, maxLng: prev.maxLng, page: 1, pageSize: prev.pageSize }))
        }
      />
      <ServiceFiltersModal
        open={serviceFiltersOpen}
        onClose={() => setServiceFiltersOpen(false)}
        filters={services.filters}
        totalCount={services.totalCount}
        onChange={(patch) => services.setFilters((prev) => ({ ...prev, ...patch, page: 1 }))}
        onClear={() =>
          services.setFilters((prev) => ({ minLat: prev.minLat, maxLat: prev.maxLat, minLng: prev.minLng, maxLng: prev.maxLng, page: 1, pageSize: prev.pageSize }))
        }
      />
    </>
  );

  return {
    activeCategory,
    /** Changes tab AND carries the current viewport over -- see switchCategory. */
    switchCategory,
    view: views[activeCategory],
    filtersModals,
    // Only used for the results-count line, which needs the plural rule and
    // nothing else -- kept here so neither screen writes it out twice.
    resultsLabel: views[activeCategory].loading
      ? t(views[activeCategory].loadingMessageKey)
      : `${views[activeCategory].totalCount} ${t(
          views[activeCategory].totalCount === 1 ? views[activeCategory].resultsSuffixKeys[1] : views[activeCategory].resultsSuffixKeys[0]
        )}`,
  };
};
